import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    registrationModeState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import { environmentState, viewManagerState } from '~/state/atoms/engineAtoms';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';

/**
 * Hook to handle registration interactions and keyboard modifiers.
 * - Installs Shift key rotation mode toggle on the React side.
 * - Listens to engine events (e.g. registration_changed).
 * - Keeps the registration tool in sync with user selection.
 */
export function useRegistrationHandler() {
    const viewManager = useRecoilValue(viewManagerState);
    const environment = useRecoilValue(environmentState);
    const selectedTool = useRecoilValue(selectedToolState);
    const [registrationOptions, setRegistrationOptions] = useRecoilState(
        registrationModeState
    );
    const registrationToolPrevMode = useRef<string | null>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                e.key === 'Shift' &&
                registrationToolPrevMode.current === null
            ) {
                registrationToolPrevMode.current = registrationOptions.mode;
                setRegistrationOptions((prev) => ({
                    ...prev,
                    mode:
                        registrationToolPrevMode.current ===
                        RegistrationMode.ROTATION
                            ? RegistrationMode.TRANSLATION
                            : RegistrationMode.ROTATION,
                }));
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (
                e.key === 'Shift' &&
                registrationToolPrevMode.current !== null
            ) {
                setRegistrationOptions((prev) => ({
                    ...prev,
                    mode: registrationToolPrevMode.current as RegistrationMode | null,
                }));
                registrationToolPrevMode.current = null;
            }
        };
        if (selectedTool === 'registration') {
            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('keyup', handleKeyUp);
            };
        }
    }, [registrationOptions.mode, setRegistrationOptions, selectedTool]);

    useEffect(() => {
        if (!viewManager || !environment) {
            return;
        }

        const handleRegistrationChange = () => {
            environment.updateSecondaryTransformation();
        };

        viewManager.addEventListener(
            'registration_changed',
            handleRegistrationChange
        );
        return () => {
            viewManager.removeEventListener(
                'registration_changed',
                handleRegistrationChange
            );
        };
    }, [viewManager, environment]);
}
